// src/pages/public/Feedback.jsx
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { 
  MessageCircle, 
  Star, 
  Send,
  User,
  AlertCircle,
  ThumbsUp,
  Award
} from 'lucide-react'
import { api } from '../../api/client'
import { endpoints } from '../../api/endpoints'

const Feedback = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rating, setRating] = useState(0)
  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  const { data: testimonials } = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get(endpoints.public.testimonials).then(res => res.data),
    staleTime: 10 * 60 * 1000,
  })

  const feedbackTypes = [
    { value: 'COMPLIMENT', label: 'Compliment', emoji: '😊' },
    { value: 'SUGGESTION', label: 'Suggestion', emoji: '💡' },
    { value: 'BUG', label: 'Bug Report', emoji: '🐛' },
    { value: 'FEATURE', label: 'Feature Request', emoji: '🚀' }
  ]

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        rating: rating || null,
        type: data.type || 'GENERAL'
      }
      await api.post(endpoints.feedback.submit, payload)
      toast.success('Feedback submitted successfully! Thank you for your input.')
      reset()
      setRating(0)
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Share Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Feedback
              </span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Your opinion matters! Help us improve YesTimeEarn by sharing your experience, 
              suggestions, or reporting any issues.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Feedback Form & Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Feedback Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl border border-gray-200 dark:border-gray-700"
            >
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Share Your Thoughts
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    How would you rate your experience? (Optional)
                  </label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className="p-2 transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= rating
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Feedback Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feedback Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {feedbackTypes.map((type) => (
                      <label
                        key={type.value}
                        className="relative flex cursor-pointer"
                      >
                        <input
                          {...register("type", { required: "Please select a feedback type" })}
                          type="radio"
                          value={type.value}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-2 w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-colors has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50 dark:has-[:checked]:bg-blue-900/20">
                          <span className="text-lg">{type.emoji}</span>
                          <span className="text-sm font-medium">{type.label}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.type && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.type.message}</span>
                    </p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    {...register("subject", { required: "Subject is required" })}
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="Brief summary of your feedback"
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.subject.message}</span>
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Detailed Feedback *
                  </label>
                  <textarea
                    {...register("message", { 
                      required: "Please provide detailed feedback",
                      minLength: {
                        value: 10,
                        message: "Feedback must be at least 10 characters"
                      }
                    })}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors resize-none"
                    placeholder="Please provide detailed feedback about your experience, suggestions for improvement, or any issues you encountered..."
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.message.message}</span>
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Submit Feedback</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Testimonials Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl p-8 text-white">
                <Award className="w-12 h-12 mb-4" />
                <h3 className="text-2xl font-bold mb-4">Why Share Feedback?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <ThumbsUp className="w-5 h-5 text-green-300" />
                    <span>Help us improve the platform</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <MessageCircle className="w-5 h-5 text-green-300" />
                    <span>Shape future features</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-green-300" />
                    <span>Get personalized support</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Star className="w-5 h-5 text-green-300" />
                    <span>Earn rewards for valuable feedback</span>
                  </li>
                </ul>
              </div>

              {/* Recent Testimonials */}
              <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  What Others Are Saying
                </h3>
                <div className="space-y-4">
                  {testimonials?.testimonials?.slice(0, 3).map((testimonial, index) => (
                    <motion.div
                      key={testimonial.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4"
                    >
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                          {testimonial.user?.username?.[0] || 'U'}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {testimonial.user?.username || 'Anonymous'}
                          </div>
                          <div className="flex items-center space-x-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < (testimonial.rating || 5)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        {testimonial.message}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Your Voice Matters
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Every piece of feedback helps us create a better investment experience for everyone
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all transform hover:scale-105"
              >
                <MessageCircle className="w-5 h-5" />
                Contact Support
              </a>
              <a
                href="/faq"
                className="border-2 border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 text-gray-700 dark:text-gray-300 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all"
              >
                Visit FAQ
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Feedback